/**
 * TicketDesk Live Chat Widget
 *
 * Shipped as a self-mounting IIFE bundle at /widget.js.  Hosts embed it
 * with a script tag and configure it via either:
 *
 *   <script src="https://td.example.com/widget.js" defer
 *           data-workspace="<UUID>"
 *           data-api="https://td.example.com/api"
 *           data-email="user@example.com"
 *           data-name="Jane Doe"
 *           data-signature="<hex>"
 *           data-timestamp="1714000000000"></script>
 *
 * or by setting `window.TicketDeskChat = { workspaceId, apiBase, email, ... }`
 * before loading the script.
 *
 * When the workspace has a chat secret configured, `data-signature` must be
 * the hex HMAC-SHA256 of `${workspaceId}:${email}:${timestamp}` (matching
 * the server's verifyIdentitySignature). The server rejects timestamps more
 * than ±5 minutes from its clock, so a signature baked into server-rendered
 * page HTML expires on long-lived pages — the widget surfaces that as an
 * inline "refresh the page" error rather than a silently disabled composer.
 */

interface ChatConfig {
  workspaceName: string;
  greeting: string;
  offlineMessage: string;
  primaryColor: string;
  position: "bottom-right" | "bottom-left";
  enabled: boolean;
  agentsOnline: boolean;
  // Whether the workspace actually sends the offline acknowledgement
  // email. We only promise "we've also emailed you" when this is true so
  // the widget never claims an email was sent when the feature is off.
  offlineAckEmailEnabled: boolean;
  // URL path-prefix -> section-key fallback map (e.g. { "/concierge":
  // "concierge" }). When NO explicit section is set (no data-section, no
  // setSection call), the widget derives the section from
  // location.pathname at session-mint time using this map — longest
  // prefix wins, matching whole path segments. Safety net for SPA hosts
  // whose router never calls setSection. Empty/missing = no fallback.
  sectionRoutePrefixes?: Record<string, string>;
}

interface WidgetMessage {
  id: string;
  type: "inbound" | "outbound" | "chat_inbound" | "chat_outbound" | "note";
  bodyHtml: string;
  bodyText: string | null;
  createdAt: string;
  // Friendly first name of the agent that sent an outbound bubble (e.g.
  // "Sarah"). Server populates this for chat_outbound / outbound rows; we
  // fall back to "Agent" / "A" in the renderer when it's missing so the
  // bubble never appears anonymous.
  agentName?: string;
  // Server flags chat_inbound bubbles authored via an email reply (rather
  // than the widget composer) so we can render a small "via email" badge
  // and the student can see which channel matched.
  viaEmail?: boolean;
  attachments?: Array<{
    id: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
    url: string | null;
  }>;
}

interface BootUser {
  email?: string;
  name?: string;
  externalId?: string;
  signature?: string;
  timestamp?: number;
}

interface BootOptions extends BootUser {
  workspaceId: string;
  apiBase: string;
  pageUrl?: string;
  locale?: string;
  // Portal-section identifier this embed represents (e.g. "compliance").
  // Sent on /chat/session so the server can file the chat into the mapped
  // inbox. Read from data-section / data-inbox / window.TicketDeskChat.section.
  section?: string;
  promptForEmail?: boolean;
  // Cadence (ms) for the while-open online-state poll (see
  // startOnlineRefresh). Defaults to 60_000; primarily an injection point so
  // tests can drive the repeating tick with a short interval rather than
  // waiting a real minute.
  onlineRefreshIntervalMs?: number;
  // Optional nested-user shape supported as an alias for the top-level
  // identity fields. Either form works; the nested form takes precedence
  // when both are provided so server-issued identity bundles drop in
  // cleanly: window.TicketDeskChat = { workspaceId, apiBase, user: {...} }.
  user?: BootUser;
  // Installed by the widget after boot. SPA hosts with ONE global embed
  // (no per-page <head>) call this on route change so a chat opened from
  // e.g. the Compliance area is filed into the Compliance inbox:
  //   window.TicketDeskChat.setSection("compliance")
  // Session tokens stay keyed per section, so switching sections never
  // reuses a token minted for a different section's inbox.
  setSection?: (section: string | null | undefined) => void;
  reset?: () => void;
  setIdentity?: (user: BootUser & { email: string }) => void;
}

declare global {
  interface Window {
    TicketDeskChat?: BootOptions;
  }
}

(function () {
  const SCRIPT = document.currentScript as HTMLScriptElement | null;

  function readBootOptions(): BootOptions | null {
    const w = window.TicketDeskChat || ({} as BootOptions);
    const ds = SCRIPT?.dataset || {};
    const workspaceId = w.workspaceId || ds.workspace || "";
    let apiBase = w.apiBase || ds.api || "";
    if (!apiBase && SCRIPT?.src) {
      try {
        const u = new URL(SCRIPT.src);
        apiBase = `${u.origin}/api`;
      } catch {
        /* ignore */
      }
    }
    if (!workspaceId || !apiBase) {
      console.warn(
        "[TicketDeskChat] Missing workspaceId or apiBase — widget will not load",
      );
      return null;
    }
    // Identity fields can be passed at the top level of TicketDeskChat OR
    // nested under a `user: { ... }` object. Nested wins on conflict — that
    // way a server-issued bundle dropped in as `user: signedIdentity`
    // overrides any earlier defaults set at the top level.
    const u: BootUser = (w.user && typeof w.user === "object") ? w.user : {};
    return {
      workspaceId,
      apiBase,
      email: u.email || w.email || ds.email,
      name: u.name || w.name || ds.name,
      externalId: u.externalId || w.externalId || ds.externalId,
      signature: u.signature || w.signature || ds.signature,
      timestamp:
        u.timestamp ||
        w.timestamp ||
        (ds.timestamp ? Number(ds.timestamp) : undefined),
      pageUrl: location.href,
      locale: navigator.language,
      // `data-inbox` is accepted as an alias for `data-section` because the
      // portal markup uses it; both carry a section KEY the server maps.
      section: w.section || ds.section || ds.inbox,
      promptForEmail: w.promptForEmail !== false,
      onlineRefreshIntervalMs:
        typeof w.onlineRefreshIntervalMs === "number" &&
        w.onlineRefreshIntervalMs > 0
          ? w.onlineRefreshIntervalMs
          : undefined,
    };
  }

  const opts = readBootOptions();
  if (!opts) return;

  // The session token is scoped per SECTION as well as per workspace: a
  // token minted on one portal section is bound server-side to that
  // section's thread/inbox, so blindly resuming it from a page embedding a
  // different section would file the new chat into the wrong inbox. A
  // distinct key per section forces a fresh /chat/session POST (which
  // re-resolves the section → inbox mapping) the first time each section
  // is visited in a tab. Section-less embeds keep the legacy key so
  // existing visitors' sessions survive this change.
  //
  // The section is MUTABLE after boot (see setSection below) because SPA
  // hosts have a single global embed and can only signal the visitor's
  // current portal area at route-change time — so the token storage key is
  // derived on demand from the current section, never captured once.
  let currentSection: string | undefined = opts.section;

  function sectionKeyPart(): string {
    return (currentSection || "").trim().toLowerCase();
  }

  /**
   * Derive a section key from the CURRENT page URL via the
   * config-delivered prefix map. Only consulted when no explicit section
   * is set (explicit always wins). Longest prefix wins; prefixes match
   * whole path segments so "/concierge" matches "/concierge" and
   * "/concierge/book" but never "/conciergerie". Returns "" when nothing
   * matches (or the config hasn't loaded yet).
   */
  function urlDerivedSectionKey(): string {
    const prefixes = cfg?.sectionRoutePrefixes;
    if (!prefixes) return "";
    let path = location.pathname.toLowerCase();
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    let bestLen = 0;
    let best = "";
    for (const rawPrefix of Object.keys(prefixes)) {
      let p = rawPrefix.trim().toLowerCase();
      if (!p) continue;
      if (!p.startsWith("/")) p = "/" + p;
      if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
      if (p === "/") continue;
      if (path !== p && !path.startsWith(p + "/")) continue;
      if (p.length > bestLen) {
        bestLen = p.length;
        best = (prefixes[rawPrefix] || "").trim().toLowerCase();
      }
    }
    return best;
  }

  /**
   * The section key the chat should ACTUALLY file under right now:
   * the explicit section (data-section / setSection) when set,
   * otherwise the URL-derived fallback. Evaluated on demand so it always
   * reflects the current SPA route at panel-open / session-mint time.
   */
  function effectiveSectionKey(): string {
    return sectionKeyPart() || urlDerivedSectionKey();
  }

  function tokenKey(): string {
    const part = effectiveSectionKey();
    return part
      ? `td_chat_token_${opts.workspaceId}_${part}`
      : `td_chat_token_${opts.workspaceId}`;
  }
  const EMAIL_KEY = `td_chat_email_${opts.workspaceId}`;
  const OWNER_KEY = `td_chat_owner_${opts.workspaceId}`;
  const RESET_KEY = `td_chat_reset_${opts.workspaceId}`;
  const identityFields = ["email", "name", "externalId", "signature", "timestamp"] as const;
  function clearStoredAccount(): void {
    const prefix = `td_chat_token_${opts.workspaceId}`;
    for (const key of Object.keys(sessionStorage)) {
      if (key === prefix || key.startsWith(prefix + "_") ||
          key === EMAIL_KEY || key === OWNER_KEY) sessionStorage.removeItem(key);
    }
  }
  function ownerOf(user: BootUser): string {
    return JSON.stringify([user.email?.trim().toLowerCase() || "", user.externalId || ""]);
  }
  // A logout marker survives reload, including stale server-rendered identity.
  let suspended = sessionStorage.getItem(RESET_KEY) === "1";
  const storedOwner = sessionStorage.getItem(OWNER_KEY);
  const bootOwner = ownerOf(opts.email ? opts : { email: sessionStorage.getItem(EMAIL_KEY) || undefined });
  if (suspended || (storedOwner && storedOwner !== bootOwner) ||
      (!storedOwner && opts.email)) clearStoredAccount();
  if (suspended) for (const field of identityFields) delete opts[field];
  if (!suspended && opts.email) sessionStorage.setItem(OWNER_KEY, ownerOf(opts));
  let accountEpoch = 0;
  let viewEpoch = 0;
  let reconnectTimer: number | null = null;

  function escape(s: string): string {
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // --- DOM scaffold (shadow DOM) ---------------------------------------
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.zIndex = "2147483646";
  host.style.bottom = "0";
  host.style.right = "0";
  host.style.left = "0";
  host.style.top = "auto";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);
  const root = host.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  styleEl.textContent = `
    :host { all: initial; }
    .wrap { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            Helvetica, Arial, sans-serif; color: #111; pointer-events: auto; }
    .bubble {
      position: fixed; bottom: 20px; width: 56px; height: 56px;
      border-radius: 9999px; box-shadow: 0 6px 18px rgba(0,0,0,.18);
      display:flex;align-items:center;justify-content:center; cursor: pointer;
      color: #fff; border: none; font-size: 26px;
      transition: transform .15s ease;
    }
    .bubble:hover { transform: scale(1.05); }
    .panel {
      position: fixed; bottom: 88px; width: 360px; max-width: calc(100vw - 32px);
      height: 520px; max-height: calc(100vh - 120px);
      border-radius: 16px; background:#fff;
      box-shadow: 0 12px 40px rgba(0,0,0,.18);
      display:flex; flex-direction: column; overflow: hidden;
      animation: tdfade .15s ease;
    }
    @keyframes tdfade {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .pos-right { right: 20px; }
    .pos-left  { left:  20px; }
    .header {
      padding: 14px 16px; color: #fff;
      display:flex; flex-direction: column;
    }
    .header-row { display:flex; align-items:center; justify-content:space-between; gap:12px; }
    .header h1 { margin: 0; font-size: 15px; font-weight: 600; }
    .header p  { margin: 2px 0 0; font-size: 12px; opacity: .85; }
    .new-conversation {
      border: 1px solid rgba(255,255,255,.65); background: rgba(255,255,255,.12);
      color: #fff; border-radius: 9999px; padding: 5px 9px; cursor: pointer;
      font: inherit; font-size: 11px; white-space: nowrap;
    }
    .new-conversation:hover { background: rgba(255,255,255,.22); }
    .new-conversation:disabled { cursor: default; opacity: .55; }
    .messages {
      flex: 1; overflow: auto; padding: 12px; background: #f6f7f9;
      display: flex; flex-direction: column; gap: 8px;
    }
    .row { display: flex; align-items: flex-end; gap: 6px; max-width: 100%; }
    .row.in  { align-self: flex-start; flex-direction: row; }
    .row.out { align-self: flex-end; flex-direction: row-reverse; }
    .col { display: flex; flex-direction: column;
           max-width: calc(100% - 32px); min-width: 0; }
    .col.in  { align-items: flex-start; }
    .col.out { align-items: flex-end; }
    .who { font-size: 11px; opacity: .6; margin: 0 4px 2px; }
    .avatar {
      width: 24px; height: 24px; border-radius: 9999px;
      display: flex; align-items: center; justify-content: center;
      font-size: 11px; font-weight: 600; color: #fff;
      flex: 0 0 24px;
    }
    .msg { max-width: 100%; padding: 8px 12px; border-radius: 12px;
           font-size: 13px; line-height: 1.4; word-wrap: break-word;
           white-space: pre-wrap; }
    .msg .body p { margin: 0; }
    .msg.in  { background: #fff;
               border-bottom-left-radius: 4px;
               box-shadow: 0 1px 1px rgba(0,0,0,.05); }
    .msg.out { color: #fff;
               border-bottom-right-radius: 4px; }
    .ts { font-size: 10px; opacity: .55; margin-top: 4px; }
    .typing { font-size: 11px; opacity: .65; padding: 0 12px 6px; }
    .composer {
      padding: 8px; border-top: 1px solid #e5e7eb; background: #fff;
      display: flex; align-items: flex-end; gap: 6px;
    }
    .composer textarea {
      flex: 1; resize: none; border: 1px solid #e5e7eb; border-radius: 8px;
      padding: 8px 10px; font: inherit; font-size: 13px; outline: none;
      max-height: 96px; min-height: 36px;
    }
    .composer textarea:focus { border-color: #888; }
    .composer button {
      border: none; border-radius: 8px; color: #fff; padding: 8px 12px;
      cursor: pointer; font: inherit; font-size: 13px;
    }
    .composer .iconbtn {
      background: transparent; color: #555; padding: 6px 8px;
      cursor: pointer; border: 1px solid transparent; border-radius: 8px;
    }
    .composer .iconbtn:hover { background: #f3f4f6; }
    .gateform {
      padding: 16px; display:flex; flex-direction:column; gap: 8px;
    }
    .gateform input {
      border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px;
      font: inherit; font-size: 13px; outline: none;
    }
    .gateform button {
      border: none; border-radius: 8px; color: #fff; padding: 9px 14px;
      cursor: pointer; font: inherit; font-size: 13px;
    }
    .att { font-size: 11px; padding: 6px 8px; background:#eef; border-radius: 6px;
           display:inline-block; margin-top: 4px; }
    .via-email {
      display: inline-flex; align-items: center; gap: 3px;
      font-size: 10px; line-height: 1; font-weight: 500;
      color: #4b5563; background: #eef2ff;
      padding: 2px 6px; border-radius: 9999px; margin-top: 4px;
    }
    .offline-note {
      margin: 0 8px 6px; padding: 8px 10px;
      background: #f3f4f6; border-left: 3px solid #9ca3af;
      border-radius: 6px; font-size: 11px; line-height: 1.4;
      color: #374151;
    }
    .err { color: #b91c1c; font-size: 12px; padding: 6px 12px; }
    .err-note {
      margin: 0 8px 6px; padding: 8px 10px;
      background: #fee2e2; border-left: 3px solid #dc2626;
      border-radius: 6px; font-size: 12px; line-height: 1.4;
      color: #7f1d1d;
    }
    .err-note button {
      margin-left: 6px; border: 1px solid #dc2626; background: #fff;
      color: #b91c1c; border-radius: 6px; padding: 2px 8px;
      font: inherit; font-size: 11px; cursor: pointer;
    }
    .upload-note {
      margin: 0 8px 6px; padding: 6px 10px;
      background: #eef2ff; border-radius: 6px; font-size: 11px;
      line-height: 1.4; color: #3730a3;
    }
    .msg.pending { opacity: .65; }
    .msg.notsent { opacity: .8; border: 1px dashed #dc2626; }
    .msg-status { font-size: 10px; opacity: .7; margin: 2px 4px 0; }
    .msg-status.msg-failed { color: #b91c1c; opacity: 1; }
    .msg-retry {
      margin-left: 6px; border: 1px solid #dc2626; background: #fff;
      color: #b91c1c; border-radius: 6px; padding: 1px 7px;
      font: inherit; font-size: 10px; cursor: pointer;
    }
    .throttle-note {
      margin: 0 8px 6px; padding: 8px 10px;
      background: #fef3c7; border-left: 3px solid #f59e0b;
      border-radius: 6px; font-size: 12px; line-height: 1.4;
      color: #78350f;
    }
  `;
  root.appendChild(styleEl);

  const wrap = document.createElement("div");
  wrap.className = "wrap";
  root.appendChild(wrap);

  // --- State ------------------------------------------------------------
  let cfg: ChatConfig | null = null;
  let isOpen = false;
  let token: string | null = sessionStorage.getItem(tokenKey());
  // The effective section key the live chat state (token / messages /
  // socket) currently belongs to. Re-checked via syncSection() whenever a
  // session could be minted or resumed (config load, panel open,
  // setSection) so SPA navigation that never calls setSection still
  // routes by the current URL once the prefix map is known.
  let activeSectionKey = effectiveSectionKey();
  let socket: WebSocket | null = null;
  let messages: WidgetMessage[] = [];
  // Append a message only if its id isn't already in the list. Both the
  // REST send-response path and the WebSocket broadcast can deliver the
  // same message, in either order — whichever arrives second must be a
  // no-op or the visitor sees their message twice.
  function appendMessage(m: WidgetMessage): boolean {
    if (messages.find((x) => x.id === m.id)) return false;
    messages.push(m);
    return true;
  }
  // Name of the agent currently typing, or null when nobody is typing.
  // We capture the name (rather than just a boolean) so the widget can show
  // "<Name> is typing…", matching the polish students see in tools like
  // Intercom or Drift.
  let agentTypingName: string | null = null;
  // Auto-clear the typing indicator after 5s of silence. The server normally
  // sends isTyping=false when the VA stops, but we don't want the indicator
  // to stick around forever if that signal is lost (closed tab, dropped
  // socket, etc.).
  let agentTypingClearTimer: number | null = null;
  let typingTimer: number | null = null;
  let lastTypingSent = 0;
  let heartbeatTimer: number | null = null;
  let promptedEmail: string | null = sessionStorage.getItem(EMAIL_KEY);
  let throttledUntilMs: number | null = null;
  let throttleTickTimer: number | null = null;
  // Once-a-minute poll of the effective-online value while the panel is
  // open, so an already-open widget flips across a business-hours boundary
  // on its own (see refreshAgentsOnline / startOnlineRefresh).
  let onlineRefreshTimer: number | null = null;
  // Session-start failure surfaced inline. Identified embeds (email
  // supplied at boot) skip the email gate entirely, so without this a
  // failed /chat/session POST left the composer permanently disabled with
  // no explanation and no retry path. `sessionErrorNeedsRefresh` marks
  // identity (403) failures where retrying with the same baked-in
  // signature/timestamp will keep failing (the ±5-minute timestamp window
  // has passed on a long-lived page) — the copy then points at a page
  // refresh, and the panel-open auto-retry is skipped to avoid pointless
  // repeat POSTs.
  let sessionError: string | null = null;
  let sessionErrorNeedsRefresh = false;
  let sessionStarting = false;
  // Automatic session-mint retry with backoff. When the panel-open session
  // POST fails (429 from monitor-churn rate-limit exhaustion, a network
  // blip, or a 5xx), the widget must show a "couldn't connect — retrying"
  // state and retry on its own instead of leaving a dead composer. 403
  // identity failures are excluded (retrying an expired baked-in signature
  // is pointless). Backoff doubles 2s → 30s cap; a 429 uses the server's
  // Retry-After instead.
  let sessionRetryTimer: number | null = null;
  let sessionRetryDelayMs = 0;
  // Locally-composed messages that have not been confirmed (201) by the
  // server yet. A message is rendered from `messages` ONLY after the
  // server persisted it; until then it lives here as "sending", and any
  // failure (no session, 429, non-201, network error) flips it to
  // "failed" with a visible Retry affordance — the widget must never show
  // a message as delivered when it wasn't persisted.
  interface OutboxItem {
    localId: string;
    text: string;
    attachments?: Array<{
      r2Key: string;
      filename: string;
      contentType: string;
      sizeBytes: number;
    }>;
    status: "sending" | "failed";
    error?: string;
    // Failed due to rate limiting — retried automatically when the
    // cooldown expires (see applyThrottle's tick).
    autoRetry?: boolean;
  }
  let outbox: OutboxItem[] = [];
  let outboxSeq = 0;
  // Attachment upload feedback: filename in flight (shows an "Uploading…"
  // indicator and disables the attach button) and the last upload error
  // (rendered inline — previously failures only console.warn'd).
  let uploadingName: string | null = null;
  let uploadError: string | null = null;

  function isThrottled(): boolean {
    return throttledUntilMs !== null && throttledUntilMs > Date.now();
  }

  function throttleRemainingSec(): number {
    if (!throttledUntilMs) return 0;
    return Math.max(0, Math.ceil((throttledUntilMs - Date.now()) / 1000));
  }

  function readRetryAfterSeconds(r: Response): number {
    // Headers may be a real Headers instance (browser fetch) or a duck-typed
    // { get } shim from tests. Default to 30s when absent so a stuck server
    // can't make us spin forever with a 1s retry loop.
    try {
      const headers = r.headers as { get?: (n: string) => string | null };
      const raw = headers?.get?.("Retry-After");
      if (raw) {
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return Math.ceil(n);
      }
    } catch {
      /* ignore */
    }
    return 30;
  }

  function applyThrottle(retryAfterSec: number): void {
    const epoch = viewEpoch;
    const newUntil = Date.now() + Math.max(1, retryAfterSec) * 1000;
    // Extend if a longer throttle arrives, never shrink an active cooldown.
    if (!throttledUntilMs || newUntil > throttledUntilMs) {
      throttledUntilMs = newUntil;
    }
    if (throttleTickTimer) clearInterval(throttleTickTimer);
    // Re-render every second so the countdown updates and the composer
    // re-enables itself the moment the cooldown expires.
    throttleTickTimer = window.setInterval(() => {
      if (epoch !== viewEpoch) return;
      if (!isThrottled()) {
        if (throttleTickTimer) clearInterval(throttleTickTimer);
        throttleTickTimer = null;
        throttledUntilMs = null;
        // The cooldown just expired — automatically resend any messages
        // that failed with a 429 so the visitor doesn't have to notice
        // and click Retry on each one themselves.
        retryAutoOutbox();
      }
      render();
    }, 1000);
    render();
  }

  /** Resend outbox items that failed due to rate limiting. */
  function retryAutoOutbox(): void {
    for (const item of outbox) {
      if (item.status === "failed" && item.autoRetry) {
        item.autoRetry = false;
        void deliverOutboxItem(item);
      }
    }
  }

  function clearAgentTyping(): void {
    if (agentTypingClearTimer) {
      clearTimeout(agentTypingClearTimer);
      agentTypingClearTimer = null;
    }
    if (agentTypingName !== null) {
      agentTypingName = null;
      render();
    }
  }

  function pos(): "right" | "left" {
    return cfg?.position === "bottom-left" ? "left" : "right";
  }

  function render(): void {
    if (!cfg || suspended) {
      wrap.innerHTML = "";
      return;
    }
    const color = cfg.primaryColor || "#1d4ed8";
    const sidePos = pos();
    const sideClass = sidePos === "left" ? "pos-left" : "pos-right";

    // When the API has flagged the visitor with a 429, surface a friendly
    // inline notice with a live countdown and disable the composer so they
    // stop hammering. We compute the banner HTML once for re-use in both
    // the gate-form and the chat-panel branches.
    const throttled = isThrottled();
    const throttleSecs = throttled ? throttleRemainingSec() : 0;
    const throttleBannerHtml = throttled
      ? `<div class="throttle-note" id="td-throttle" role="status" aria-live="polite">You're sending messages a little too fast — please wait ${throttleSecs}s before trying again.</div>`
      : "";

    // Inline session-start failure notice. The plain variant renders above
    // the email gate (resubmitting the gate IS the retry there); the
    // composer variant adds an explicit "Try again" button because
    // identified embeds have no gate to fall back to.
    const sessionErrorText = sessionError
      ? `${sessionError}${
          sessionErrorNeedsRefresh
            ? " If this keeps happening, refresh the page."
            : ""
        }`
      : "";
    const sessionErrorPlainHtml = sessionError
      ? `<div class="err-note" id="td-session-error" role="alert">${escape(sessionErrorText)}</div>`
      : "";
    const sessionErrorRetryHtml = sessionError
      ? `<div class="err-note" id="td-session-error" role="alert">${escape(sessionErrorText)}<button type="button" id="td-retry" ${sessionStarting ? "disabled" : ""}>Try again</button></div>`
      : "";

    // Attachment upload progress / failure. Only one shows at a time: an
    // in-flight upload clears any earlier error, and a new error clears
    // the in-flight name.
    const uploadNoteHtml = uploadingName
      ? `<div class="upload-note" id="td-uploading" role="status" aria-live="polite">Uploading ${escape(uploadingName)}…</div>`
      : uploadError
        ? `<div class="err-note" id="td-upload-error" role="alert">${escape(uploadError)}</div>`
        : "";

    let panelHtml = "";
    if (isOpen) {
      const needsEmail = !token && !opts.email && !promptedEmail;
      panelHtml = `
        <div class="panel ${sideClass}">
          <div class="header" style="background:${color}">
            <div class="header-row">
              <h1>${escape(cfg.workspaceName || "Live Chat")}</h1>
              ${
                token && messages.length > 0
                  ? `<button type="button" class="new-conversation" id="td-new-conversation" ${
                      sessionStarting || outbox.length > 0 || Boolean(uploadingName)
                        ? "disabled"
                        : ""
                    }>New conversation</button>`
                  : ""
              }
            </div>
            <p>${escape(cfg.greeting)}</p>
          </div>
          ${
            needsEmail
              ? `
            ${throttleBannerHtml}
            ${sessionErrorPlainHtml}
            <form class="gateform" id="td-gate">
              <label style="font-size:12px">Your email</label>
              <input id="td-email" type="email" required placeholder="you@example.com" value="${escape(sessionStorage.getItem(EMAIL_KEY) || "")}" ${throttled ? "disabled" : ""} />
              <label style="font-size:12px">Your name (optional)</label>
              <input id="td-name" type="text" placeholder="Name" ${throttled ? "disabled" : ""} />
              <button type="submit" style="background:${color}" ${throttled ? "disabled" : ""}>Start chatting</button>
            </form>
          `
              : `
            <div class="messages" id="td-list">
              ${messages
                .map((m) => {
                  const isOut =
                    m.type === "chat_outbound" || m.type === "outbound";
                  const ts = new Date(m.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  const atts = (m.attachments || [])
                    .map(
                      (a) =>
                        `<a class="att" target="_blank" rel="noopener" href="${
                          a.url || "#"
                        }">${escape(a.filename)}</a>`,
                    )
                    .join("");
                  // Server tags chat_inbound bubbles authored via an
                  // email reply with viaEmail=true so the student sees
                  // the channel match — without it both look identical.
                  const viaEmailBadge = m.viaEmail
                    ? `<div class="via-email" title="Sent via email reply">✉ via email</div>`
                    : "";
                  const side = isOut ? "out" : "in";
                  const bubble = `<div class="msg ${side}" ${
                    isOut ? `style="background:${color}"` : ""
                  }>
                    <div class="body">${m.bodyHtml || escape(m.bodyText || "")}</div>
                    ${atts}
                    ${viaEmailBadge}
                    <div class="ts">${ts}</div>
                  </div>`;
                  if (!isOut) {
                    // Inbound bubbles (from the student themself) don't
                    // need an avatar — they already know who they are.
                    return `<div class="row in"><div class="col in">${bubble}</div></div>`;
                  }
                  // Outbound: show the agent's first name + initial
                  // avatar so the student sees who is replying, the way
                  // Intercom/Drift do. Falls back to "Agent" / "A" when
                  // the server didn't supply a name.
                  const rawName =
                    typeof m.agentName === "string" && m.agentName.trim()
                      ? m.agentName.trim()
                      : "Agent";
                  const initial =
                    rawName.charAt(0).toUpperCase() || "A";
                  return `<div class="row out">
                    <div class="avatar" style="background:${color}" title="${escape(
                      rawName,
                    )}">${escape(initial)}</div>
                    <div class="col out">
                      <div class="who">${escape(rawName)}</div>
                      ${bubble}
                    </div>
                  </div>`;
                })
                .join("")}
              ${outbox
                .map((o) => {
                  // Locally-composed, not-yet-confirmed messages. Render on
                  // the visitor's side with an explicit status line — a
                  // bubble here is NEVER presented as delivered: it either
                  // shows "Sending…" or a "Not delivered" error + Retry.
                  const attNote = o.attachments?.length
                    ? `<span class="att">${escape(o.attachments[0].filename)}</span>`
                    : "";
                  const statusHtml =
                    o.status === "sending"
                      ? `<div class="msg-status">Sending…</div>`
                      : `<div class="msg-status msg-failed" role="alert">${escape(
                          o.error || "Not delivered.",
                        )}<button type="button" class="msg-retry" data-local-id="${escape(
                          o.localId,
                        )}">Retry</button></div>`;
                  return `<div class="row in"><div class="col in">
                    <div class="msg in ${o.status === "sending" ? "pending" : "notsent"}">
                      <div class="body">${escape(o.text)}</div>
                      ${attNote}
                    </div>
                    ${statusHtml}
                  </div></div>`;
                })
                .join("")}
            </div>
            ${
              !cfg.agentsOnline
                ? `<div class="offline-note">${escape(cfg.offlineMessage)}${
                    cfg.offlineAckEmailEnabled
                      ? `<br /><strong>We've also emailed you</strong> — replies to that email will appear here.`
                      : ""
                  }</div>`
                : agentTypingName
                  ? `<div class="typing">${escape(agentTypingName)} is typing…</div>`
                  : ""
            }
            ${throttleBannerHtml}
            ${sessionErrorRetryHtml}
            ${uploadNoteHtml}
            <form class="composer" id="td-form">
              <button type="button" class="iconbtn" id="td-attach"
                title="Attach file (max 25MB)" ${
                  token && !throttled && !uploadingName ? "" : "disabled"
                }>📎</button>
              <input type="file" id="td-file" style="display:none" />
              <textarea id="td-text" rows="1" placeholder="${
                throttled
                  ? `Please wait ${throttleSecs}s…`
                  : cfg.agentsOnline
                    ? "Type a message…"
                    : "Leave a message — we'll reply by email."
              }"
                ${token && !throttled ? "" : "disabled"}></textarea>
              <button type="submit" style="background:${color}"
                ${token && !throttled ? "" : "disabled"}>Send</button>
            </form>
          `
          }
        </div>`;
    }

    wrap.innerHTML = `
      ${panelHtml}
      <button class="bubble ${sideClass}" id="td-bubble"
        style="background:${color}" aria-label="Open chat">${
      isOpen ? "×" : "💬"
    }</button>
    `;

    const bubble = root.getElementById("td-bubble") as HTMLButtonElement;
    bubble?.addEventListener("click", () => {
      isOpen = !isOpen;
      render();
      if (isOpen) {
        // SPA route may have changed since the last open without any
        // setSection call — re-derive the effective section from the URL
        // (no-op when unchanged) BEFORE deciding to resume or mint, so
        // the token below already belongs to the current section.
        syncSection(false);
        if (token) loadHistoryAndConnect();
        else if (
          opts.email &&
          !sessionStarting &&
          !isThrottled() &&
          !sessionErrorNeedsRefresh
        ) {
          // Identified embed with no session yet: this is now the ONLY
          // place a session is minted for identified embeds (boot no
          // longer POSTs /chat/session — see fetchConfig), so a pageview
          // that never opens the chat never creates a server session.
          // Identity (403) failures are excluded — the baked-in signature
          // won't get any fresher, so we keep the inline "refresh the
          // page" error instead of hammering the server.
          void startSession({ email: opts.email, name: opts.name });
        }
        // Correct any boot value that went stale before the panel opened,
        // then keep it fresh across schedule boundaries while open.
        void refreshAgentsOnline();
        startOnlineRefresh();
      } else {
        stopOnlineRefresh();
      }
    });

    const gate = root.getElementById("td-gate") as HTMLFormElement | null;
    gate?.addEventListener("submit", (e) => {
      e.preventDefault();
      // Defence-in-depth guard mirroring the one in sendMessage: the
      // submit button is already disabled in the rendered HTML during a
      // cooldown, but a programmatic submit() (or rapid double-click)
      // could still slip through without this short-circuit.
      if (isThrottled()) return;
      const email = (root.getElementById("td-email") as HTMLInputElement).value;
      const name = (root.getElementById("td-name") as HTMLInputElement).value;
      // Persist the typed email up-front so it pre-populates the gate
      // input on re-render (e.g. after a 429 forces the gate back). The
      // in-memory `promptedEmail` flag suppresses the gate while the
      // /chat/session POST is in flight; if it 429s we clear that flag
      // inside startSession so the gate re-renders with the throttle
      // banner instead of stranding the visitor with a disabled composer.
      promptedEmail = email;
      sessionStorage.setItem(EMAIL_KEY, email);
      sessionStorage.setItem(OWNER_KEY, ownerOf({ email }));
      void startSession({ email, name });
    });

    const form = root.getElementById("td-form") as HTMLFormElement | null;
    const ta = root.getElementById("td-text") as HTMLTextAreaElement | null;
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!ta) return;
      const text = ta.value.trim();
      if (!text) return;
      ta.value = "";
      void sendMessage(text);
    });
    ta?.addEventListener("input", () => {
      sendTyping();
    });

    const list = root.getElementById("td-list") as HTMLDivElement | null;
    if (list) list.scrollTop = list.scrollHeight;

    const retryBtn = root.getElementById("td-retry");
    retryBtn?.addEventListener("click", () => {
      const email =
        opts.email || promptedEmail || sessionStorage.getItem(EMAIL_KEY);
      if (!email || sessionStarting) return;
      void startSession({ email, name: opts.name });
    });

    const newConversationBtn = root.getElementById(
      "td-new-conversation",
    ) as HTMLButtonElement | null;
    newConversationBtn?.addEventListener("click", () => {
      if (sessionStarting || outbox.length > 0 || uploadingName) return;
      const email =
        opts.email || promptedEmail || sessionStorage.getItem(EMAIL_KEY);
      if (!email) return;
      if (
        !window.confirm(
          "Start a new conversation? This chat will stay in your ticket history.",
        )
      ) {
        return;
      }
      void startSession({
        email,
        name: opts.name,
        newConversation: true,
      });
    });

    // Per-message Retry buttons on failed (undelivered) outbox bubbles.
    wrap.querySelectorAll(".msg-retry").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = (btn as HTMLElement).getAttribute("data-local-id");
        const item = outbox.find((o) => o.localId === id);
        if (item && item.status === "failed") void deliverOutboxItem(item);
      });
    });

    const attachBtn = root.getElementById("td-attach");
    const fileInput = root.getElementById("td-file") as HTMLInputElement | null;
    attachBtn?.addEventListener("click", () => fileInput?.click());
    fileInput?.addEventListener("change", () => {
      const f = fileInput.files?.[0];
      if (!f) return;
      void uploadAndSend(f);
      fileInput.value = "";
    });
  }

  async function fetchConfig(): Promise<void> {
    const url = `${opts.apiBase}/chat/config?workspaceId=${encodeURIComponent(
      opts.workspaceId,
    )}`;
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) throw new Error(`Failed to load chat config (${r.status})`);
    cfg = (await r.json()) as ChatConfig;
    if (!cfg.enabled) {
      cfg = null;
      return;
    }
    render();
    // The prefix map just arrived: if the boot URL matches one and no
    // explicit section is set, retarget the (idle) chat state at the
    // URL-derived section so the token key is right before first open.
    syncSection(false);
    // Deliberately NO session creation here. Sessions are minted lazily on
    // panel open (see the bubble click handler) — an identified embed used
    // to POST /chat/session on every page load, which meant every pageview
    // by a monitor/bot/headless browser created a server session + rate-
    // limit hit even though the chat was never opened. (In production this
    // showed up as an automated portal health probe minting ~4 junk
    // sessions every ~5 minutes around the clock.)
  }

  /**
   * Re-fetch the public chat config and apply only its dynamic
   * `agentsOnline` flag to the live widget. The online/offline UI is
   * otherwise set once at boot, so when a workspace runs on a fixed
   * business-hours schedule the visible state goes stale the moment the
   * wall clock crosses an open/close boundary: the boot value was fetched
   * before the boundary, and the server's `chat:agents-online` push only
   * reaches widgets with a connected session socket (which connects lazily
   * on panel open) — so a widget opened just after a boundary, or one that
   * missed the push, would keep showing the pre-boundary state until reload.
   *
   * Polling the server's already-computed effective-online value keeps the
   * widget in lockstep with GET /chat/config without duplicating the
   * business-hours math into the widget (which would add a fourth place
   * that has to agree on "is the workspace online right now").
   */
  async function refreshAgentsOnline(): Promise<void> {
    const epoch = viewEpoch;
    if (!cfg) return;
    try {
      const url = `${opts.apiBase}/chat/config?workspaceId=${encodeURIComponent(
        opts.workspaceId,
      )}`;
      const r = await fetch(url, { credentials: "omit" });
      if (!r.ok) return;
      const next = (await r.json()) as ChatConfig;
      if (epoch !== viewEpoch) return;
      if (!cfg || !next.enabled) return;
      if (cfg.agentsOnline !== next.agentsOnline) {
        cfg.agentsOnline = next.agentsOnline;
        // Going offline drops any stale "is typing…" indicator.
        if (!cfg.agentsOnline) clearAgentTyping();
        render();
      }
    } catch {
      // Network blip — keep the last known state rather than flapping.
    }
  }

  /**
   * While the panel is open, poll the effective-online value once a minute
   * so an already-open widget flips across a business-hours boundary on its
   * own, with no reload — mirroring the admin Business Hours badge's client
   * tick. The WS `chat:agents-online` push still delivers near-instant
   * updates when a session socket is connected; this is the belt-and-
   * suspenders that also covers the no-session and missed-push cases.
   */
  function startOnlineRefresh(): void {
    const epoch = viewEpoch;
    if (onlineRefreshTimer !== null) return;
    const intervalMs = opts.onlineRefreshIntervalMs ?? 60_000;
    onlineRefreshTimer = window.setInterval(() => {
      if (isOpen && epoch === viewEpoch) void refreshAgentsOnline();
    }, intervalMs);
  }

  function stopOnlineRefresh(): void {
    if (onlineRefreshTimer !== null) {
      clearInterval(onlineRefreshTimer);
      onlineRefreshTimer = null;
    }
  }

  /**
   * Schedule an automatic session-mint retry. Used for 429s (delay from
   * the server's Retry-After), network errors, and 5xxs (exponential
   * backoff 2s → 30s cap). Only fires while the panel is open with no
   * session; needs a known email (identified embed or a previously
   * submitted gate) — pure gate embeds retry by resubmitting the form.
   */
  function scheduleSessionRetry(delayMs?: number): void {
    const epoch = viewEpoch;
    if (sessionRetryTimer !== null) return;
    const email =
      opts.email || promptedEmail || sessionStorage.getItem(EMAIL_KEY);
    if (!email) return;
    sessionRetryDelayMs =
      delayMs ?? Math.min(Math.max(sessionRetryDelayMs * 2, 2000), 30_000);
    sessionRetryTimer = window.setTimeout(() => {
      if (epoch !== viewEpoch || suspended) return;
      sessionRetryTimer = null;
      if (!isOpen || token || sessionStarting || sessionErrorNeedsRefresh) {
        return;
      }
      void startSession({ email, name: opts.name });
    }, sessionRetryDelayMs);
  }

  async function startSession(args: {
    email: string;
    name?: string;
    newConversation?: boolean;
  }): Promise<void> {
    if (suspended) return;
    const epoch = accountEpoch;
    const view = viewEpoch;
    const body: Record<string, unknown> = {
      workspaceId: opts.workspaceId,
      email: args.email,
      name: args.name,
      pageUrl: opts.pageUrl,
      locale: opts.locale,
    };
    if (opts.externalId) body.externalId = opts.externalId;
    if (opts.signature) body.signature = opts.signature;
    if (opts.timestamp) body.timestamp = opts.timestamp;
    if (args.newConversation) body.newConversation = true;
    // Explicit section (data-section / setSection) always wins; only when
    // none is set do we fall back to the URL-derived section from the
    // config's sectionRoutePrefixes map — evaluated NOW, at mint time, so
    // it reflects the SPA route the visitor is actually on. The source is
    // sent alongside so server logs can tell the two paths apart.
    if (sectionKeyPart()) {
      body.section = currentSection;
      body.sectionSource = "explicit";
    } else {
      const urlSection = urlDerivedSectionKey();
      if (urlSection) {
        body.section = urlSection;
        body.sectionSource = "url_fallback";
      }
    }
    if (sessionStarting) return;
    // A manual or scheduled retry is now running — cancel any pending
    // auto-retry timer so we never end up with two concurrent mints.
    if (sessionRetryTimer !== null) {
      clearTimeout(sessionRetryTimer);
      sessionRetryTimer = null;
    }
    // Capture the storage key NOW: if the SPA host calls setSection while
    // this POST is in flight, the minted token belongs to the section it
    // was requested for — persist it under that key, but do NOT adopt it
    // as the live token for the (different) current section.
    const keyAtStart = tokenKey();
    sessionStarting = true;
    sessionError = null;
    sessionErrorNeedsRefresh = false;
    render();
    let r: Response;
    try {
      r = await fetch(`${opts.apiBase}/chat/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "omit",
      });
    } catch {
      if (epoch !== accountEpoch || view !== viewEpoch) return;
      // Network failure — surface it inline instead of stranding the
      // visitor with a silently disabled composer (identified embeds have
      // no gate to fall back to), and retry automatically with backoff.
      sessionStarting = false;
      sessionError = "Chat couldn't connect — retrying automatically.";
      promptedEmail = null;
      scheduleSessionRetry();
      render();
      return;
    }
    if (epoch !== accountEpoch) return;
    if (view !== viewEpoch && r.status !== 201) return;
    if (view === viewEpoch) sessionStarting = false;
    if (r.status === 429) {
      // Clear the in-memory gate-suppression flag so the gate re-renders
      // (with the throttle banner above and inputs disabled) instead of
      // dropping the visitor onto a disabled composer with no resubmit
      // path. The persisted EMAIL_KEY is left in place so the gate's
      // email input stays pre-populated after the cooldown elapses.
      // A rate-limited mint retries itself after the server's Retry-After
      // — the visitor should never have to notice the 429 at all.
      promptedEmail = null;
      const retryAfterSec = readRetryAfterSeconds(r);
      sessionError = "Chat couldn't connect — retrying automatically.";
      scheduleSessionRetry(retryAfterSec * 1000 + 500);
      applyThrottle(retryAfterSec);
      return;
    }
    // Strict 201 contract: a session exists ONLY when the server said
    // "created". Any other status — including a 2xx like 200 — must not
    // be trusted as a minted session.
    if (r.status !== 201) {
      // Same recovery rationale as the 429 path: don't strand the
      // visitor. Gate flow: the gate re-renders with the error note above
      // it and resubmitting is the retry. Identified embeds: the composer
      // branch renders the same note with an explicit "Try again" button.
      // A 403 means the identity signature was rejected — most commonly a
      // baked-in timestamp that drifted past the server's ±5-minute
      // window on a long-lived page — so the copy points at a refresh.
      // Retrying with the same rejected signature is pointless, so 403 is
      // the only failure that does NOT auto-retry.
      if (r.status === 403) {
        sessionError = "We couldn't verify your chat sign-in.";
        sessionErrorNeedsRefresh = true;
      } else {
        sessionError = "Chat couldn't connect — retrying automatically.";
        scheduleSessionRetry();
      }
      promptedEmail = null;
      render();
      console.warn("[TicketDeskChat] Failed to start session", await r.text());
      return;
    }
    const data = (await r.json()) as { sessionToken: string };
    if (epoch !== accountEpoch) return;
    if (view !== viewEpoch) {
      sessionStorage.setItem(keyAtStart, data.sessionToken);
      return;
    }
    sessionError = null;
    sessionErrorNeedsRefresh = false;
    sessionRetryDelayMs = 0;
    // Only after a replacement session is confirmed do we tear down the old
    // conversation. A failed mint leaves the visitor's current chat intact.
    if (args.newConversation) {
      token = null;
      const oldSocket = socket;
      socket = null;
      try {
        oldSocket?.close();
      } catch {
        /* ignore */
      }
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      messages = [];
      outbox = [];
      clearAgentTyping();
    }
    sessionStorage.setItem(keyAtStart, data.sessionToken);
    if (keyAtStart !== tokenKey()) {
      // Section changed while the POST was in flight — the token is
      // stored for its own section (resumable when the visitor returns
      // there) but must not drive the current section's chat.
      render();
      return;
    }
    token = data.sessionToken;
    await loadHistoryAndConnect();
    // loadHistoryAndConnect renders on success; make sure the composer
    // re-enables even if history fetch bailed early (e.g. throttle).
    render();
  }

  async function loadHistoryAndConnect(): Promise<void> {
    const epoch = viewEpoch;
    const requestedToken = token;
    if (!token) return;
    let r: Response;
    try {
      r = await fetch(`${opts.apiBase}/chat/messages?limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "omit",
      });
    } catch { return; }
    if (epoch !== viewEpoch || requestedToken !== token) return;
    if (r.status === 429) {
      applyThrottle(readRetryAfterSeconds(r));
      return;
    }
    if (!r.ok) {
      if (r.status === 401) {
        token = null;
        sessionStorage.removeItem(tokenKey());
        render();
      }
      return;
    }
    const data = (await r.json()) as { messages: WidgetMessage[] };
    if (epoch !== viewEpoch || requestedToken !== token) return;
    messages = data.messages || [];
    render();
    connectSocket();
    startHeartbeat();
  }

  function connectSocket(): void {
    const epoch = viewEpoch;
    if (!token) return;
    if (socket && socket.readyState === WebSocket.OPEN) return;
    const apiUrl = new URL(opts.apiBase);
    const wsProto = apiUrl.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProto}//${apiUrl.host}/api/ws?session=${encodeURIComponent(
      token,
    )}`;
    socket = new WebSocket(wsUrl);
    const ownSocket = socket;
    socket.addEventListener("message", (e) => {
      if (epoch !== viewEpoch || socket !== ownSocket) return;
      try {
        const msg = JSON.parse(e.data as string);
        if (msg.type === "ping") {
          socket?.send(JSON.stringify({ type: "pong" }));
          return;
        }
        if (msg.type === "event") {
          if (msg.event === "chat:message") {
            const m = msg.data as WidgetMessage;
            if (appendMessage(m)) {
              // A delivered agent message means the agent stopped typing,
              // so drop any active "is typing…" indicator immediately.
              const isAgentMessage =
                m.type === "chat_outbound" || m.type === "outbound";
              if (isAgentMessage) {
                clearAgentTyping();
              }
              render();
            }
          } else if (msg.event === "chat:typing:agent") {
            const isTyping = !!msg.data?.isTyping;
            if (isTyping) {
              const name =
                typeof msg.data?.agentName === "string" &&
                msg.data.agentName.trim()
                  ? (msg.data.agentName as string)
                  : "An agent";
              agentTypingName = name;
              if (agentTypingClearTimer) {
                clearTimeout(agentTypingClearTimer);
              }
              // Auto-hide after 5s if no further typing pings arrive.
              agentTypingClearTimer = window.setTimeout(() => {
                if (epoch !== viewEpoch || socket !== ownSocket) return;
                agentTypingClearTimer = null;
                agentTypingName = null;
                render();
              }, 5000);
              render();
            } else {
              clearAgentTyping();
            }
          } else if (msg.event === "chat:agents-online") {
            if (cfg) {
              cfg.agentsOnline = !!msg.data?.online;
              // When agents go offline, clear any stale typing state.
              if (!cfg.agentsOnline) clearAgentTyping();
              render();
            }
          }
        }
      } catch {
        /* ignore */
      }
    });
    socket.addEventListener("close", () => {
      if (epoch !== viewEpoch || socket !== ownSocket) return;
      socket = null;
      if (token) {
        // Reconnect with backoff.
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          if (epoch === viewEpoch && !suspended) connectSocket();
        }, 3000);
      }
    });
  }

  function startHeartbeat(): void {
    const epoch = viewEpoch;
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = window.setInterval(() => {
      if (!token || epoch !== viewEpoch) return;
      void fetch(`${opts.apiBase}/chat/heartbeat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "omit",
      })
        .then((r) => {
          if (epoch !== viewEpoch) return;
          if (r.status === 429) applyThrottle(readRetryAfterSeconds(r));
        })
        .catch(() => {});
    }, 60_000);
  }

  function sendTyping(): void {
    const epoch = viewEpoch;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastTypingSent > 2000) {
      socket.send(JSON.stringify({ type: "contact_typing", isTyping: true }));
      lastTypingSent = now;
    }
    if (typingTimer) clearTimeout(typingTimer);
    typingTimer = window.setTimeout(() => {
      if (epoch !== viewEpoch) return;
      socket?.send(JSON.stringify({ type: "contact_typing", isTyping: false }));
      lastTypingSent = 0;
    }, 3000);
  }

  /**
   * Queue a composed message and attempt delivery. The message is
   * rendered immediately as a "Sending…" bubble and only becomes a normal
   * (delivered) bubble after the server confirms with a 201; every
   * failure path leaves it visible in a "Not delivered" state with a
   * Retry button. Returns true only on confirmed delivery.
   */
  async function sendMessage(
    text: string,
    attachments?: Array<{
      r2Key: string;
      filename: string;
      contentType: string;
      sizeBytes: number;
    }>,
  ): Promise<boolean> {
    const item: OutboxItem = {
      localId: `local-${Date.now()}-${++outboxSeq}`,
      text,
      attachments,
      status: "sending",
    };
    outbox.push(item);
    render();
    return deliverOutboxItem(item);
  }

  /**
   * Try to deliver one outbox item. Every failure marks the item
   * "failed" (visible, retryable) — never a silent drop:
   *   - no session token → failed ("not connected")
   *   - active throttle / POST 429 → failed + auto-resend when the
   *     cooldown expires
   *   - network error or non-201 → failed with manual Retry
   * A 201 removes the item from the outbox and appends the confirmed
   * server message.
   */
  async function deliverOutboxItem(item: OutboxItem): Promise<boolean> {
    const epoch = viewEpoch;
    if (!token) {
      item.status = "failed";
      item.error = "Not connected — tap Retry once chat reconnects.";
      render();
      return false;
    }
    if (isThrottled()) {
      item.status = "failed";
      item.autoRetry = true;
      item.error = "Sending too fast — will retry automatically.";
      render();
      return false;
    }
    item.status = "sending";
    item.error = undefined;
    render();
    let r: Response;
    try {
      r = await fetch(`${opts.apiBase}/chat/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bodyText: item.text, attachments: item.attachments }),
        credentials: "omit",
      });
    } catch {
      if (epoch !== viewEpoch) return false;
      item.status = "failed";
      item.error = "Couldn't send — check your connection.";
      render();
      return false;
    }
    if (epoch !== viewEpoch) return false;
    if (r.status === 429) {
      item.status = "failed";
      item.autoRetry = true;
      item.error = "Sending too fast — will retry automatically.";
      applyThrottle(readRetryAfterSeconds(r));
      return false;
    }
    // Strict 201 contract: the message is delivered ONLY on "created".
    // Any other status (even 200/202/204) leaves the bubble in the
    // visible failed state rather than pretending it was sent.
    if (r.status !== 201) {
      item.status = "failed";
      item.error = "Message not delivered.";
      render();
      console.warn("[TicketDeskChat] Send failed", await r.text());
      return false;
    }
    const m = (await r.json()) as WidgetMessage;
    if (epoch !== viewEpoch) return false;
    outbox = outbox.filter((o) => o.localId !== item.localId);
    appendMessage(m);
    render();
    return true;
  }

  async function uploadAndSend(file: File): Promise<void> {
    const epoch = viewEpoch;
    // Mirrors the composer gating: the attach button is disabled without a
    // token / during a throttle / while an upload is in flight, but a
    // programmatic call could still slip through.
    if (!token || isThrottled() || uploadingName) return;
    uploadError = null;
    if (file.size > 25 * 1024 * 1024) {
      uploadError = `"${file.name}" is larger than the 25MB limit.`;
      render();
      return;
    }
    uploadingName = file.name;
    render();
    const fd = new FormData();
    fd.append("file", file);
    let up: Response;
    try {
      up = await fetch(`${opts.apiBase}/chat/attachments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
        credentials: "omit",
      });
    } catch {
      if (epoch !== viewEpoch) return;
      uploadingName = null;
      uploadError = "Upload failed. Check your connection and try again.";
      render();
      return;
    }
    if (epoch !== viewEpoch) return;
    uploadingName = null;
    if (up.status === 429) {
      applyThrottle(readRetryAfterSeconds(up));
      return;
    }
    if (!up.ok) {
      uploadError = "Upload failed. Please try again.";
      render();
      console.warn("[TicketDeskChat] Upload failed", await up.text());
      return;
    }
    const data = (await up.json()) as {
      r2Key: string;
      filename: string;
      contentType: string;
      sizeBytes: number;
    };
    if (epoch !== viewEpoch) return;
    // Delivery failures now surface on the message bubble itself (failed
    // state + Retry), so no separate upload-error banner is needed here.
    await sendMessage(file.name, [data]);
    render();
  }

  /**
   * Switch the portal section this widget files new chats under. For SPA
   * hosts with a single global embed: call on route change, e.g.
   * window.TicketDeskChat.setSection("compliance"). No-op when the
   * (normalized) section is unchanged. Otherwise the current chat is torn
   * down and the session token for the new section — if one was minted
   * earlier this tab — is resumed; a missing token means the next panel
   * open (or immediately, if the panel is already open) mints a fresh
   * session, which is where the server re-resolves section → inbox.
   */
  function setSection(section: string | null | undefined): void {
    const next = typeof section === "string" ? section : undefined;
    const nextKeyPart = (next || "").trim().toLowerCase();
    if (nextKeyPart === sectionKeyPart()) {
      // Same explicit section (after normalization) — keep the raw value
      // fresh, then let syncSection decide whether anything effective
      // changed (only possible when both are empty and the URL moved).
      currentSection = next || currentSection;
    } else {
      currentSection = next;
    }
    syncSection(true);
  }

  /**
   * Re-evaluate the EFFECTIVE section (explicit section, else the
   * URL-derived fallback) and, when it changed, retarget the live chat
   * state at the new section. No-op when unchanged. Called from
   * setSection, from fetchConfig once the prefix map is known, and on
   * every panel open (so SPA navigation that never calls setSection
   * still routes by the current URL at session-mint time).
   *
   * `resume` mirrors the old setSection behaviour: when true and the
   * panel is open, the new section's session is resumed or (identified
   * embeds) minted immediately. The bubble-open caller passes false
   * because its own resume/mint logic runs right after.
   */
  function syncSection(resume: boolean): void {
    const nextKey = effectiveSectionKey();
    if (nextKey === activeSectionKey) return;
    activeSectionKey = nextKey;
    clearLiveState();
    // Resume the new section's own token if this tab minted one earlier.
    token = sessionStorage.getItem(tokenKey());
    if (resume && isOpen) {
      startOnlineRefresh();
      // Mirror the bubble-open flow: resume the section's session, or
      // mint one for identified embeds (gate embeds re-show the gate /
      // composer via render below and mint on submit).
      if (token) void loadHistoryAndConnect();
      else if (
        opts.email &&
        !sessionStarting &&
        !isThrottled() &&
        !sessionErrorNeedsRefresh
      ) {
        void startSession({ email: opts.email, name: opts.name });
      }
    }
    render();
  }

  // Expose the SPA section API on the boot global (creating it for
  // dataset-only embeds that never defined window.TicketDeskChat).
  const publicBoot: BootOptions =
    window.TicketDeskChat || (window.TicketDeskChat = { ...opts });
  publicBoot.setSection = setSection;
  publicBoot.reset = reset;
  publicBoot.setIdentity = setIdentity;
  if (suspended) reset();
  window.dispatchEvent(new Event("ticketdesk:ready"));

  /** Invalidate callbacks before closing the socket (close can fire synchronously). */
  function clearLiveState(): void {
    viewEpoch++;
    token = null;
    const oldSocket = socket;
    socket = null;
    try { oldSocket?.close(); } catch { /* already closed */ }
    for (const timer of [agentTypingClearTimer, typingTimer, heartbeatTimer,
      throttleTickTimer, onlineRefreshTimer, sessionRetryTimer, reconnectTimer]) {
      if (timer !== null) {
        clearTimeout(timer);
        clearInterval(timer);
      }
    }
    agentTypingClearTimer = typingTimer = heartbeatTimer = throttleTickTimer =
      onlineRefreshTimer = sessionRetryTimer = reconnectTimer = null;
    messages = [];
    outbox = [];
    uploadingName = uploadError = agentTypingName = sessionError = null;
    sessionStarting = sessionErrorNeedsRefresh = false;
    throttledUntilMs = null;
    sessionRetryDelayMs = lastTypingSent = outboxSeq = 0;
    // Remove detached drafts/file inputs too; no old render is reused.
    wrap.innerHTML = "";
  }

  function reset(): void {
    accountEpoch++;
    suspended = true;
    isOpen = false;
    clearLiveState();
    promptedEmail = null;
    for (const field of identityFields) {
      delete opts[field];
      delete publicBoot[field];
      if (SCRIPT) delete SCRIPT.dataset[field];
    }
    delete publicBoot.user;
    clearStoredAccount();
    sessionStorage.setItem(RESET_KEY, "1");
    render();
  }

  function setIdentity(user: BootUser & { email: string }): void {
    if (!user || typeof user.email !== "string" || !user.email.trim()) {
      throw new TypeError("TicketDeskChat.setIdentity requires an email");
    }
    // Copy before reset: callers may pass the boot object itself.
    const fresh: BootUser = {};
    for (const field of identityFields) {
      if (user[field] !== undefined) Object.assign(fresh, { [field]: user[field] });
    }
    reset();
    Object.assign(opts, fresh);
    publicBoot.user = { ...fresh };
    sessionStorage.setItem(OWNER_KEY, ownerOf(fresh));
    sessionStorage.removeItem(RESET_KEY);
    suspended = false;
    render(); // closed; only an explicit panel open creates a session
  }

  // Boot.
  fetchConfig().catch((err) => {
    console.warn("[TicketDeskChat] init failed", err);
  });
})();
